import './PrivacyPolicyPage.css';

export default function PrivacyPolicyPage() {
  return (
    <main className="privacy-page">
      <div className="privacy-hero">
        <div className="privacy-hero-inner">
          <h1>Privacy Policy for Trenda</h1>
          <p className="privacy-updated">Last updated: 18 March 2026</p>
        </div>
      </div>

      <div className="privacy-body">
        <article className="privacy-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to Trenda (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;). We respect your privacy and are
              committed to protecting your personal information in accordance with the UK General
              Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p>
              Trenda is a mobile app designed to help people living with diabetes record and review
              their blood glucose readings, food intake, and exercise activity. This privacy policy
              explains how we handle your information.
            </p>
          </section>

          <section>
            <h2>2. Summary</h2>
            <ul>
              <li>All your health data is stored only on your device.</li>
              <li>We do not have access to or store your data on any external servers.</li>
              <li>
                When you use features that contact third-party APIs, data is encrypted in transit and
                not stored externally.
              </li>
              <li>You can delete all data at any time by uninstalling the app.</li>
            </ul>
          </section>

          <section>
            <h2>3. Information We Collect</h2>
            <p>
              Trenda only collects information you actively enter into the app, such as:
            </p>
            <ul>
              <li>Blood glucose readings</li>
              <li>Meals and nutritional notes</li>
              <li>Exercise and activity details</li>
              <li>Optional health notes</li>
            </ul>
            <p>
              No other personal identifiers (like your name, email, or contact details) are required
              to use the app.
            </p>
          </section>

          <section>
            <h2>4. Where Your Data Is Stored</h2>
            <p>
              All information you enter in Trenda is stored locally on your device. Neither we nor any
              third party have access to your data. If you back up your device via iCloud or another
              service, that backup may include your data under your own control.
            </p>
          </section>

          <section>
            <h2>5. Data Use and Processing</h2>
            <p>
              Your data is used solely within the app for the purpose of displaying reports, trends,
              and insights.
            </p>
            <p>
              Occasionally, the app may send small, anonymised requests to third-party APIs (for example,
              to process food items or activities). When this occurs:
            </p>
            <ul>
              <li>Only the minimum data required is sent;</li>
              <li>All data is transmitted via HTTPS (encrypted);</li>
              <li>The data is not stored by us or by the third party;</li>
              <li>
                No personally identifiable or health information is shared.
              </li>
            </ul>
            <p>
              We never use your data for analytics, advertising, or user profiling.
            </p>
          </section>

          <section>
            <h2>6. Lawful Basis for Processing</h2>
            <p>
              Under the UK GDPR, our lawful basis for processing your data is your explicit consent,
              provided when you record information in the app. You can withdraw consent at any time by
              deleting your entries or uninstalling the app.
            </p>
          </section>

          <section>
            <h2>7. Your Data Protection Rights</h2>
            <p>Under UK GDPR, you have the following rights:</p>
            <ul>
              <li>Right of access &ndash; to view the information you&rsquo;ve entered.</li>
              <li>Right to rectification &ndash; to correct inaccurate entries.</li>
              <li>Right to erasure &ndash; to delete your data (achieved by uninstalling the app).</li>
              <li>Right to withdraw consent &ndash; to cease any further processing by stopping use of the app.</li>
              <li>
                Right to lodge a complaint &ndash; with the Information Commissioner&rsquo;s Office (ICO) if you feel
                your data is mishandled.
              </li>
            </ul>
            <p>
              Because all data remains on your device, you exercise these rights directly via your phone.
            </p>
          </section>

          <section>
            <h2>8. Security</h2>
            <p>Trenda employs modern security practices to keep your data private:</p>
            <ul>
              <li>Data is securely stored on your device;</li>
              <li>All communications are sent over secure, encrypted connections (HTTPS);</li>
              <li>No data is collected for remote analytics, diagnostics, or advertising.</li>
            </ul>
          </section>

          <section>
            <h2>9. Children&rsquo;s Privacy</h2>
            <p>
              Trenda is intended for adult users managing diabetes or caregivers. It is not designed for children
              under 13. We do not knowingly collect information from children.
            </p>
          </section>

          <section>
            <h2>10. Data Controller</h2>
            <p>
              <strong>Data Controller:</strong>
              <br />
              Trenda (Developed by Futurist Ventures Ltd)
            </p>
            <p>
              As we do not store or process identifiable personal data externally, a Data Protection Officer is not required.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this privacy notice occasionally to reflect improvements or regulatory changes. The latest
              version will always be available within the app or on our website.
            </p>
          </section>

          <section>
            <h2>12. Contact</h2>
            <p>
              If you have any questions or concerns regarding this policy or your data rights, please contact:
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:info@futurist-ventures.co.uk">info@futurist-ventures.co.uk</a>
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint with the
              Information Commissioner&rsquo;s Office (ICO):
            </p>
            <p>
              <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">
                www.ico.org.uk
              </a>
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}

